import React from "react";
import { Link, Navigate } from "react-router-dom";
import { BookOpen, FileText, Info } from "lucide-react";
import SalesAffiliateLayout from "../components/SalesAffiliateLayout";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";
import { SALES_AFFILIATE_WORKSPACE } from "../lib/salesAffiliateWorkspaceConfig";

const VIDEO_ID = "hM84vrVaSBU";
const INSURANCE_VIDEO_ID = "yYvAUx0Pq7w";

export default function SalesAffiliateLearningCenter() {
  const {
    loading,
    statusLoadFailed,
    errorMessage,
    configuration,
    refreshWorkspace,
  } = useSalesAffiliateWorkspace();

  if (loading) {
    return (
      <SalesAffiliateLayout activeSection="learning-center">
        <div className="flex min-h-[500px] items-center justify-center">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 shadow-sm">
            Loading Learning Center…
          </div>
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (statusLoadFailed) {
    return (
      <SalesAffiliateLayout activeSection="learning-center">
        <div className="mx-auto flex min-h-[500px] max-w-2xl items-center px-8">
          <div className="w-full rounded-3xl border border-amber-200 bg-amber-50 p-6 text-amber-950 shadow-sm">
            <div className="text-sm font-black">Learning Center unavailable</div>
            <p className="mt-2 text-sm leading-6">
              We could not safely confirm your Sales Affiliate access.
            </p>
            {errorMessage ? (
              <p className="mt-2 text-xs opacity-80">{errorMessage}</p>
            ) : null}
            <button
              type="button"
              onClick={() => refreshWorkspace()}
              className="mt-4 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
            >
              Retry
            </button>
          </div>
        </div>
      </SalesAffiliateLayout>
    );
  }

  const allowed =
    configuration.workspaceState === SALES_AFFILIATE_WORKSPACE.ONBOARDING ||
    configuration.workspaceState === SALES_AFFILIATE_WORKSPACE.ACTIVE;

  if (!allowed) {
    return <Navigate to={configuration.defaultPath} replace />;
  }

  return (
    <SalesAffiliateLayout activeSection="learning-center">
      <div className="mx-auto max-w-6xl px-8 py-10">
        <div className="flex items-center gap-3">
          <BookOpen className="text-blue-600" size={28} />
          <div>
            <div className="text-xs font-black uppercase tracking-[0.32em] text-blue-600">
              Optional Sales Affiliate Resources
            </div>
            <h1 className="mt-2 text-4xl font-black tracking-tight text-slate-950">
              Sales Affiliate Learning Center
            </h1>
          </div>
        </div>

        <p className="mt-5 max-w-4xl text-base leading-7 text-slate-600">
          Practical training and support to help you represent AI Fine Protector
          professionally and confidently. We’ll continue adding new videos,
          guidance, and support materials over time, so check back periodically
          for new resources.
        </p>

        <div className="mt-8 flex gap-3 rounded-3xl border border-blue-100 bg-blue-50 px-5 py-5 text-blue-950">
          <Info className="mt-0.5 flex-none text-blue-600" size={22} />
          <div>
            <div className="text-sm font-black">Optional and non-gating</div>
            <p className="mt-1 text-sm leading-6">
              Learning Center resources support your Sales Affiliate work but do
              not count toward Assigned Training or account activation.
            </p>
          </div>
        </div>

        <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="text-2xl font-black tracking-tight text-slate-950">
            Why we ask about your industries
          </h2>
          <p className="mt-4 max-w-4xl text-sm leading-7 text-slate-600">
            We ask about the industries you work in so we can develop more
            relevant training, examples, and support materials for the markets
            you know best. Over time, the Learning Center may include
            industry-specific videos and guidance tailored to different types of
            prospects and businesses.
          </p>
        </section>

        <section className="mt-8 rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6 shadow-sm sm:p-8">
          <div className="text-xs font-black uppercase tracking-[0.24em] text-blue-600">
            Subscriber Experience Walkthrough
          </div>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">
            What Subscribers Experience in AFP
          </h2>
          <p className="mt-3 max-w-4xl text-sm leading-7 text-slate-600">
            See the subscriber journey from Dashboard and the 60-question Assessment
            through results, Reports, Remediation, evidence documentation, and
            reassessment. Use this walkthrough to explain AFP accurately and credibly.
          </p>
          <Link
            to="/sales-affiliate/learning-center/subscriber-experience"
            className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-blue-700"
          >
            <BookOpen size={18} />
            Open Subscriber Experience Walkthrough
          </Link>
        </section>

        <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="text-xs font-black uppercase tracking-[0.24em] text-blue-600">
            Printable Resource
          </div>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">
            Sales Affiliate Field Guide
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            You can download and print this field guide. It may be useful to keep
            nearby as you get started and begin having prospect conversations.
          </p>
          <a
            href="/sales-affiliate/AI_Fine_Protector_Sales_Affiliate_Field_Guide.pdf"
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800"
          >
            <FileText size={18} />
            View / Download Sales Affiliate Field Guide (PDF)
          </a>
        </section>

        <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="text-xs font-black uppercase tracking-[0.24em] text-blue-600">
            Video 01
          </div>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">
            How to Introduce AI Fine Protector to a Prospect
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            A practical introduction to discussing AI Fine Protector with a
            prospective customer.
          </p>

          <div className="mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}`}
                title="How to Introduce AI Fine Protector to a Prospect"
                loading="lazy"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="text-xs font-black uppercase tracking-[0.24em] text-blue-600">
            Video 02 · Insurance Industry
          </div>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">
            How Insurance Companies Can Assess &amp; Reduce AI Risk Exposure Before Underwriting
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            See how insurers can use AI Fine Protector to identify and quantify
            AI regulatory exposure before underwriting, support better risk
            selection, and help insureds address material gaps.
          </p>

          <div className="mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${INSURANCE_VIDEO_ID}`}
                title="How Insurance Companies Can Assess and Reduce AI Risk Exposure Before Underwriting"
                loading="lazy"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      </div>
    </SalesAffiliateLayout>
  );
}
